import { useEffect, useState } from 'react';
import { api } from '../api';
import { ErrorNotice, Notice, Spinner } from './ui';
import type { Setting } from '../types';

/**
 * The handful of things the committee can decide for themselves.
 *
 * Each one carries its own explanation from the server, so what a volunteer
 * reads cannot drift away from what the setting actually does. Nothing secret
 * is here — passwords and API keys stay in the config file on the server.
 */
export function SettingsCard() {
  const [settings, setSettings] = useState<Setting[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ settings: Setting[] }>('/settings')
      .then((r) => setSettings(r.settings))
      .catch(setError);
  }, []);

  async function toggle(setting: Setting, next: boolean) {
    setError(null);
    setSaving(setting.key);
    try {
      const result = await api.put<{ settings: Setting[] }>('/settings', {
        settings: { [setting.key]: next },
      });
      setSettings(result.settings);
      setSaved(setting.key);
      window.setTimeout(() => setSaved(null), 4000);
    } catch (failure) {
      setError(failure);
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="card">
      <h2>What the association asks for</h2>

      <ErrorNotice error={error} />
      {settings === null && error === null ? <Spinner label="Loading settings" /> : null}

      {(settings ?? []).map((setting) => (
        <div key={setting.key} className="setting">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={setting.value === true}
              disabled={saving === setting.key}
              onChange={(e) => void toggle(setting, e.target.checked)}
            />
            {setting.label}
          </label>
          <p className="hint">{setting.description}</p>
          {saved === setting.key ? <Notice kind="success">Saved.</Notice> : null}
        </div>
      ))}

      <p className="muted small">
        Changing one of these is recorded in the audit log, with who changed it and when.
      </p>
    </div>
  );
}
